import type { Paginated } from '@fdt/shared';
import { api } from './client';

/** Query parameters every list endpoint understands, plus per-resource filters. */
export interface ListParams {
  page: number;
  pageSize: number;
  search?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  [filter: string]: string | number | boolean | undefined;
}

/**
 * Thin typed wrapper over one REST collection. Entities differ in their fields,
 * not in their endpoints, so the shapes are parameterised and the paths are not.
 */
export function resource<TItem, TCreate, TUpdate = Partial<TCreate>>(path: string) {
  return {
    path,

    async list(params: ListParams): Promise<Paginated<TItem>> {
      // Undefined filters must not become "undefined" strings in the query.
      const query = Object.fromEntries(
        Object.entries(params).filter(([, value]) => value !== undefined && value !== ''),
      );
      const { data } = await api.get<Paginated<TItem>>(path, { params: query });
      return data;
    },

    async get(id: string): Promise<TItem> {
      const { data } = await api.get<TItem>(`${path}/${id}`);
      return data;
    },

    async create(body: TCreate): Promise<TItem> {
      const { data } = await api.post<TItem>(path, body);
      return data;
    },

    async update(id: string, body: TUpdate): Promise<TItem> {
      const { data } = await api.patch<TItem>(`${path}/${id}`, body);
      return data;
    },

    async remove(id: string): Promise<void> {
      await api.delete(`${path}/${id}`);
    },
  };
}

export type Resource<TItem, TCreate, TUpdate = Partial<TCreate>> = ReturnType<
  typeof resource<TItem, TCreate, TUpdate>
>;
